import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { usePendingPayment } from '@/hooks/use-pending-payment';
import { 
  CreditCard, 
  Clock, 
  X, 
  AlertTriangle,
  CheckCircle 
} from 'lucide-react';

interface PendingPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PendingPaymentModal: React.FC<PendingPaymentModalProps> = ({
  isOpen,
  onClose
}) => {
  const { toast } = useToast();
  const { 
    pendingPayments, 
    isLoading, 
    clearPendingPayment,
    checkPendingPayments 
  } = usePendingPayment();

  const handleClearPayment = async (paymentId: string) => {
    try {
      await clearPendingPayment(paymentId);
      toast({
        title: 'Payment Cleared',
        description: 'Pending payment has been cleared successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to clear pending payment.',
        variant: 'destructive'
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentTypeColor = (type: string) => {
    switch (type) {
      case 'subscription':
        return 'bg-blue-100 text-blue-800';
      case 'course':
        return 'bg-green-100 text-green-800';
      case 'resource':
        return 'bg-purple-100 text-purple-800';
      case 'connect':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Pending Payments
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">Loading pending payments...</p>
            </div>
          ) : pendingPayments.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Pending Payments</h3>
              <p className="text-muted-foreground">You don't have any pending payments at the moment.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                <p className="text-sm text-muted-foreground">
                  You have {pendingPayments.length} pending payment{pendingPayments.length !== 1 ? 's' : ''} that need attention.
                </p>
              </div>

              <div className="space-y-3">
                {pendingPayments.map((payment) => (
                  <Card key={payment.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={getPaymentTypeColor(payment.type)}>
                              {payment.type.charAt(0).toUpperCase() + payment.type.slice(1)}
                            </Badge>
                            <span className="font-semibold text-lg">
                              {formatCurrency(payment.amount)}
                            </span>
                          </div>
                          
                          <h4 className="font-medium mb-1">
                            {payment.description || `${payment.type} Payment`}
                          </h4>
                          
                          {payment.course && (
                            <p className="text-sm text-muted-foreground mb-1">
                              Course: {payment.course.title}
                            </p>
                          )}
                          
                          {payment.resource && (
                            <p className="text-sm text-muted-foreground mb-1">
                              Resource: {payment.resource.title}
                            </p>
                          )}
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              Created: {formatDate(payment.createdAt)}
                            </div>
                            
                            {payment.expiresAt && (
                              <div className="flex items-center gap-1">
                                <AlertTriangle className="h-4 w-4" />
                                Expires: {formatDate(payment.expiresAt)}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 ml-4">
                          <Button
                            size="sm"
                            onClick={() => {
                              // Redirect to payment completion
                              if (payment.metadata?.checkoutUrl) {
                                window.location.href = payment.metadata.checkoutUrl;
                              } else {
                                toast({
                                  title: 'Payment Link Unavailable',
                                  description: 'Please try creating a new payment.',
                                  variant: 'destructive'
                                });
                              }
                            }}
                          >
                            Complete Payment
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleClearPayment(payment.id)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Clear
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex justify-between items-center pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={checkPendingPayments}
                  disabled={isLoading}
                >
                  Refresh
                </Button>
                
                <div className="text-sm text-muted-foreground">
                  Total pending: {formatCurrency(
                    pendingPayments.reduce((sum, payment) => sum + payment.amount, 0)
                  )}
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
